import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PrepwiseLogo from './PrepwiseLogo';
import './Header.css';

interface HeaderProps {
  collectionsCount?: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Header: React.FC<HeaderProps> = ({ collectionsCount = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated, organization } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [profileName, setProfileName] = useState(user?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  const isPublicPage = !isAuthenticated || location.pathname === '/login' || location.pathname === '/admin/signup';
  const isAdmin = user?.role === 'admin';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update local state when user changes
  useEffect(() => {
    setProfileName(user?.name || '');
  }, [user?.name]);

  const handleSaveProfile = async () => {
    if (!user?.email || !profileName.trim()) return;
    
    setIsSaving(true);
    try {
      // Update name via API
      const response = await fetch(`${API_URL}/organizations/${user.organizationId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          name: profileName.trim(),
          role: user.role,
        }),
      });
      
      if (response.ok) {
        // Update local storage
        const updatedUser = { ...user, name: profileName.trim() };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        window.location.reload(); // Simple refresh to update state
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
      setEditProfileOpen(false);
    }
  };

  const getDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };
  
  // Hide chat for employees when taking tests (prevent cheating)
  const isTakingTest = location.pathname.includes('/take') || 
                       location.pathname.includes('/employee/tests/') ||
                       location.pathname.includes('/assignments/');

  const handleLogoClick = () => {
    if (isAuthenticated) {
      navigate(isAdmin ? '/admin/dashboard' : '/employee/dashboard');
    } else {
      navigate('/');
    }
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <header className={`app-header ${isPublicPage ? 'public' : 'authenticated'}`}>
      <div className="header-container">
        <div className="header-brand" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <PrepwiseLogo size="medium" />
          <span className="header-logo-text">Prepwise</span>
        </div>

        {/* Hamburger Menu Button */}
        <button 
          className="hamburger-menu"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
        </button>

        {isPublicPage ? (
          // Public Navigation
          <nav className={`header-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a 
              href="/" 
              onClick={(e) => { e.preventDefault(); handleNavClick('/'); }}
              className={location.pathname === '/' ? 'active' : ''}
            >
              <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              <span>Home</span>
            </a>
            <a 
              href="/features" 
              onClick={(e) => { e.preventDefault(); handleNavClick('/features'); }}
              className={location.pathname === '/features' ? 'active' : ''}
            >
              <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
              </svg>
              <span>Features</span>
            </a>
            <a 
              href="/contact" 
              onClick={(e) => { e.preventDefault(); handleNavClick('/contact'); }}
              className={location.pathname === '/contact' ? 'active' : ''}
            >
              <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span>Contact</span>
            </a>
          </nav>
        ) : (
          // Authenticated Navigation
          <>
            <nav className={`header-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              {isAdmin ? (
                <>
                  <a 
                    href="/admin/dashboard" 
                    onClick={(e) => { e.preventDefault(); handleNavClick('/admin/dashboard'); }}
                    className={location.pathname === '/admin/dashboard' ? 'active' : ''}
                  >
                    <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    <span>Dashboard</span>
                  </a>
                  <a 
                    href="/admin/tests/create" 
                    onClick={(e) => { e.preventDefault(); handleNavClick('/admin/tests/create'); }}
                    className={location.pathname.includes('/admin/tests') && !location.pathname.includes('/results') ? 'active' : ''}
                  >
                    <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    <span>Tests</span>
                  </a>
                  <a 
                    href="/admin/tests/results" 
                    onClick={(e) => { e.preventDefault(); handleNavClick('/admin/tests/results'); }}
                    className={location.pathname === '/admin/tests/results' ? 'active' : ''}
                  >
                    <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                    <span>Results</span>
                  </a>
                </>
              ) : (
                <>
                  <a 
                    href="/employee/dashboard" 
                    onClick={(e) => { e.preventDefault(); handleNavClick('/employee/dashboard'); }}
                    className={location.pathname === '/employee/dashboard' ? 'active' : ''}
                  >
                    <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    <span>Dashboard</span>
                  </a>
                </>
              )}
              {/* Hide chat for employees when taking tests */}
              {(isAdmin || !isTakingTest) && (
                <a 
                  href="/chat" 
                  onClick={(e) => { e.preventDefault(); handleNavClick('/chat'); }}
                  className={location.pathname === '/chat' ? 'active' : ''}
                >
                  <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <span>Chat</span>
                </a>
              )}
              {/* Mobile Profile Section */}
              <div className="mobile-profile-section desktop-logout-hidden">
                <div className="mobile-profile-header">
                  <div className="mobile-profile-avatar">
                    {getDisplayName().charAt(0).toUpperCase()}
                  </div>
                  <div className="mobile-profile-info">
                    <span className="mobile-profile-name">{getDisplayName()}</span>
                    {organization && (
                      <span className="mobile-profile-org">{organization.name}</span>
                    )}
                  </div>
                </div>
              </div>
              <button 
                className="mobile-menu-item desktop-logout-hidden" 
                onClick={() => {
                  setEditProfileOpen(true);
                  setMobileMenuOpen(false);
                }}
              >
                <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>Edit Profile</span>
              </button>
              <button 
                className="mobile-menu-item logout-item desktop-logout-hidden" 
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
              >
                <svg className="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span>Logout</span>
              </button>
            </nav>
            <div className={`header-user-info ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              {/* Profile Dropdown */}
              <div className="profile-dropdown-container desktop-only" ref={profileDropdownRef}>
                <button 
                  className="profile-button"
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                >
                  <span className="profile-name">{getDisplayName()}</span>
                  <svg className="profile-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                
                {profileDropdownOpen && (
                  <div className="profile-dropdown">
                    <div className="profile-dropdown-header">
                      <div className="profile-avatar">
                        {getDisplayName().charAt(0).toUpperCase()}
                      </div>
                      <div className="profile-info">
                        <span className="profile-dropdown-name">{getDisplayName()}</span>
                        <span className="profile-dropdown-email">{user?.email}</span>
                        {organization && (
                          <span className="profile-dropdown-org">{organization.name}</span>
                        )}
                      </div>
                    </div>
                    <div className="profile-dropdown-divider" />
                    <button 
                      className="profile-dropdown-item"
                      onClick={() => {
                        setEditProfileOpen(true);
                        setProfileDropdownOpen(false);
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      Edit Profile
                    </button>
                    <div className="profile-dropdown-divider" />
                    <button 
                      className="profile-dropdown-item logout"
                      onClick={() => {
                        logout();
                        setProfileDropdownOpen(false);
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                        <polyline points="16 17 21 12 16 7"></polyline>
                        <line x1="21" y1="12" x2="9" y2="12"></line>
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>

          </>
        )}
      </div>

      {/* Edit Profile Modal - rendered via portal to avoid header positioning issues */}
      {editProfileOpen && createPortal(
        <div className="profile-modal-overlay" onClick={() => setEditProfileOpen(false)}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h3>Edit Profile</h3>
              <button 
                className="profile-modal-close"
                onClick={() => setEditProfileOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="profile-modal-body">
              <div className="profile-form-group">
                <label htmlFor="profileName">Display Name</label>
                <input
                  type="text"
                  id="profileName"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Enter your name"
                />
              </div>
              <div className="profile-form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="disabled"
                />
              </div>
              <div className="profile-form-group">
                <label>Role</label>
                <input
                  type="text"
                  value={user?.role || ''}
                  disabled
                  className="disabled"
                />
              </div>
              {organization && (
                <div className="profile-form-group">
                  <label>Organization</label>
                  <input
                    type="text"
                    value={organization.name}
                    disabled
                    className="disabled"
                  />
                </div>
              )}
            </div>
            <div className="profile-modal-footer">
              <button 
                className="profile-modal-cancel"
                onClick={() => setEditProfileOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="profile-modal-save"
                onClick={handleSaveProfile}
                disabled={isSaving || !profileName.trim()}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Header;

